import { Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { BENCHMARK_DESCRIPTION_MAX_LENGTH, BENCHMARK_NAME_MAX_LENGTH } from '@vizit/constants';
import { Dialog, TextAreaField, TextField } from '@vizit/molecules';
import * as React from 'react';

export interface BenchmarkEditValues {
  name: string;
  description: string | null;
}

export interface BenchmarkEditDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Called when the dialog opens or closes
   */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * The benchmark's current name and description, filled in each time the dialog opens
   */
  initialValues: BenchmarkEditValues;
  /**
   * Error from the last save, shown under the name
   *
   * @optional
   */
  errorMessage?: string | null;
  /**
   * Saves the trimmed values; the dialog stays open and shows `errorMessage` if this rejects
   */
  onSubmit: (values: BenchmarkEditValues) => Promise<void>;
}

/**
 * Rename a benchmark or change its description. The image type is locked at creation, so it is not offered here.
 * Laid out like the create dialog (`FormDialog`): the same field molecules, spacing and footer.
 */
export function BenchmarkEditDialog(props: BenchmarkEditDialogProps) {
  const { isOpen, onOpenChange, initialValues, errorMessage, onSubmit } = props;

  const [name, setName] = React.useState(initialValues.name);
  const [description, setDescription] = React.useState(initialValues.description ?? '');
  const [isSaving, setIsSaving] = React.useState(false);

  // The fields remount with the saved values each time the dialog opens; reset the tracked edits to match.
  React.useEffect(() => {
    if (isOpen) {
      setName(initialValues.name);
      setDescription(initialValues.description ?? '');
    }
  }, [isOpen, initialValues.name, initialValues.description]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const hasChanges = trimmedName !== initialValues.name || trimmedDescription !== (initialValues.description ?? '');
  const canSave = trimmedName.length > 0 && hasChanges && !isSaving;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSubmit({ name: trimmedName, description: trimmedDescription || null });
    } catch {
      // The caller reports the failure through `errorMessage`; keep the dialog open with the edits.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Overlay isDismissable={!isSaving}>
        <Dialog.Content closeButtonIsDisabled={isSaving}>
          <Dialog.Header>
            <Dialog.Title>Edit benchmark</Dialog.Title>
          </Dialog.Header>
          <form
            className="flex min-h-0 flex-1 flex-col px-12"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSave) {
                void handleSave();
              }
            }}
          >
            <div className="flex flex-col gap-10">
              <TextField
                label="Benchmark name"
                isRequired
                defaultValue={initialValues.name}
                onChange={setName}
                maxLength={BENCHMARK_NAME_MAX_LENGTH}
                isInvalid={!!errorMessage}
                errorMessage={errorMessage}
                autoFocus
              />
              <TextAreaField
                label={
                  <>
                    Description <span className="font-regular text-tertiary">(optional)</span>
                  </>
                }
                defaultValue={initialValues.description ?? ''}
                onChange={setDescription}
                placeholder="What is this benchmark for?"
                rows={3}
                maxLength={BENCHMARK_DESCRIPTION_MAX_LENGTH}
              />
            </div>
            <Dialog.Footer className="mx-0">
              <Button
                className="w-full"
                size={ButtonSize.Lg}
                level={ButtonLevel.Secondary}
                onPress={() => onOpenChange(false)}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                size={ButtonSize.Lg}
                level={ButtonLevel.Primary}
                type="submit"
                isDisabled={!canSave}
                isLoading={isSaving}
              >
                Save changes
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}
